import { Link } from 'react-router-dom';
import { Leaf, Radio, ShieldCheck, MapPin, Zap, Eye, Target, Globe, TrendingDown } from 'lucide-react';
import './VisionPage.css';

const pillars = [
  { Icon: Leaf,       label: 'Zero Emission',  sub: 'EV-only fleet' },
  { Icon: Radio,      label: 'Real-Time',       sub: 'Full traceability' },
  { Icon: ShieldCheck,label: 'GDP Compliant',   sub: 'Healthcare-grade' },
  { Icon: MapPin,     label: 'Thailand First',  sub: 'Built for local needs' },
];

const commitments = [
  { Icon: Leaf,         num:'01', title:'Decarbonize Logistics',              desc:'Replace traditional diesel fleets with EV-powered transport, reducing carbon emissions across urban and regional delivery networks.' },
  { Icon: Eye,          num:'02', title:'Enhance Supply Chain Visibility',    desc:'Deliver real-time data, IoT sensors, and digital platforms that give businesses full transparency over their supply chain.' },
  { Icon: Target,       num:'03', title:'Deliver Mission-Critical Goods',     desc:'Start with healthcare and medical supplies where reliability, compliance, and traceability are absolutely essential.' },
  { Icon: Globe,        num:'04', title:'Build a Scalable EV Ecosystem',      desc:'Create a logistics infrastructure that supports multiple industries — healthcare, food, and FMCG — under one smart platform.' },
  { Icon: TrendingDown, num:'05', title:'Achieve Cost Parity',                desc:'Provide pricing competitive with traditional logistics while delivering superior technology, sustainability, and service quality.' },
];

export default function VisionPage() {
  return (
    <div className="vision-page">

      {/* ── HERO ── */}
      <section className="vp-hero">
        <div className="vp-hero-grid" />
        <div className="vp-hero-content">
          <span className="vp-eyebrow">Our Vision</span>
          <h1 className="vp-title">
            One of the Best<br />
            Logistics Networks<br />
            <span className="vp-accent">in Thailand</span>
          </h1>
          <p className="vp-desc">
            Axion Mobility envisions a future where logistics is zero-emission, fully traceable,
            and digitally connected — enabling businesses to deliver goods faster, safer, and more
            efficiently while reducing environmental impact.
          </p>
          <div className="vp-pillars">
            {pillars.map(({ Icon, label, sub }) => (
              <div key={label} className="vp-pillar">
                <div className="vp-pillar-icon"><Icon size={20} strokeWidth={1.7} /></div>
                <p className="vp-pillar-label">{label}</p>
                <p className="vp-pillar-sub">{sub}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── THE BIG PICTURE ── */}
      <section className="vp-big">
        <div className="vp-big-left">
          <span className="vp-eyebrow">The Big Picture</span>
          <h2>A Cleaner, Smarter,<br />More Reliable Future</h2>
          <p>
            Axion Mobility is building the infrastructure for tomorrow's supply chain — one that is
            fully electric, deeply connected, and built to the highest standards of reliability.
          </p>
          <p>
            Starting with healthcare logistics in Thailand, where compliance and traceability are
            non-negotiable, we are proving that sustainable logistics and operational excellence
            can go hand in hand.
          </p>
          <div className="vp-vehicle-badge">
            <div className="vp-badge-icon"><Zap size={18} /></div>
            <div>
              <p className="vp-badge-title">EV Cold-Chain Pickup</p>
              <p className="vp-badge-sub">Healthcare-grade temperature control · GDP compliant</p>
            </div>
          </div>
        </div>
        <div className="vp-big-right">
          <div className="vp-stat-stack">
            <div className="vp-stat-card">
              <strong>Zero</strong><span>Carbon Emissions</span>
            </div>
            <div className="vp-stat-card">
              <strong>100%</strong><span>Digital Traceability</span>
            </div>
            <div className="vp-stat-card">
              <strong>GDP</strong><span>Healthcare Compliant</span>
            </div>
            <div className="vp-stat-card vp-stat-card--accent">
              <strong>#1</strong><span>Target: Best EV Logistics in Thailand</span>
            </div>
          </div>
        </div>
      </section>

      {/* ── MISSION ── */}
      <section className="vp-mission">
        <div className="vp-mission-header">
          <span className="vp-eyebrow">Our Mission</span>
          <h2>Five Core Commitments</h2>
          <p>Every decision at Axion Mobility is guided by these five principles that define who we are and how we operate.</p>
        </div>
        <div className="vp-commitments">
          {commitments.map(({ Icon, num, title, desc }) => (
            <div key={num} className="vp-commitment">
              <div className="vpc-left">
                <div className="vpc-icon"><Icon size={22} strokeWidth={1.7} /></div>
                <span className="vpc-num">{num}</span>
              </div>
              <div className="vpc-right">
                <h3>{title}</h3>
                <p>{desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="vp-cta">
        <img src="/axion-logo.png" alt="Axion Mobility" className="vp-cta-logo" />
        <h2>Aligned with Our Vision?</h2>
        <p>Join us in building a cleaner, smarter logistics network for Thailand and beyond.</p>
        <Link to="/contact" className="vp-cta-btn">Become a Partner →</Link>
        <Link to="/" className="vp-back">← Back to Overview</Link>
      </section>

    </div>
  );
}
